/**
 * Security settings page (MFA)
 *
 * Minimal UI to enroll/verify/disable TOTP MFA using /api/auth/mfa/* endpoints.
 * No QR rendering to avoid new dependencies; users can scan via otpauth URI or enter secret manually.
 */

import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ShieldCheck, ShieldOff, KeyRound, Copy } from 'lucide-react';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import { authApi, ApiError } from '../lib/api';
import { authKeys } from '../hooks/useAuth';
import { useAppStore } from '../stores/appStore';
import { useSEO, SEO_CONFIGS } from '../hooks/useSEO';

type EnrollData = {
  secret: string;
  otpauthUri: string;
  backupCodes: string[];
  message: string;
};

async function copyToClipboard(text: string): Promise<void> {
  if (typeof navigator === 'undefined' || !navigator.clipboard) return;
  await navigator.clipboard.writeText(text);
}

export default function SecurityPage() {
  // SEO meta tags for security page
  useSEO(SEO_CONFIGS.security);

  const user = useAppStore((s) => s.user);
  const addNotification = useAppStore((s) => s.addNotification);
  const queryClient = useQueryClient();

  const [enroll, setEnroll] = useState<EnrollData | null>(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [disableCode, setDisableCode] = useState('');

  const mfaEnabled = !!user?.mfaEnabledAt;

  const enrollMutation = useMutation({
    mutationFn: async () => {
      const res = await authApi.mfaEnroll();
      return res.data as EnrollData;
    },
    onSuccess: (data) => {
      setEnroll(data);
      addNotification({
        type: 'info',
        message: data.message || 'MFA iniciado. Confirme com o código do seu autenticador.',
        duration: 8000,
      });
    },
    onError: (error: ApiError) => {
      addNotification({ type: 'error', message: error.message || 'Erro ao iniciar MFA', duration: 6000 });
    },
  });

  const verifyMutation = useMutation({
    mutationFn: async () => {
      const res = await authApi.mfaVerify(verifyCode.trim());
      return res.data as { enabled: boolean };
    },
    onSuccess: async () => {
      setVerifyCode('');
      setEnroll(null);
      await queryClient.invalidateQueries({ queryKey: authKeys.me() });
      addNotification({ type: 'success', message: 'MFA habilitado com sucesso', duration: 5000 });
    },
    onError: (error: ApiError) => {
      addNotification({ type: 'error', message: error.message || 'Código inválido', duration: 6000 });
    },
  });

  const disableMutation = useMutation({
    mutationFn: async () => {
      const res = await authApi.mfaDisable(disableCode.trim());
      return res.data as { disabled: boolean };
    },
    onSuccess: async () => {
      setDisableCode('');
      setEnroll(null);
      await queryClient.invalidateQueries({ queryKey: authKeys.me() });
      addNotification({ type: 'success', message: 'MFA desabilitado', duration: 5000 });
    },
    onError: (error: ApiError) => {
      addNotification({ type: 'error', message: error.message || 'Código inválido', duration: 6000 });
    },
  });

  const backupCodesText = useMemo(() => (enroll?.backupCodes || []).join('\n'), [enroll]);

  return (
    <div className="max-w-2xl mx-auto space-y-8">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-full bg-primary-50 text-primary-600 flex items-center justify-center dark:bg-primary-950 dark:text-primary-300">
          <ShieldCheck className="w-6 h-6" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">Segurança</h1>
          <p className="text-neutral-600 dark:text-neutral-400">Gerencie MFA (autenticador) e códigos de backup.</p>
        </div>
      </div>

      {!mfaEnabled ? (
        <div className="card p-6 space-y-4">
          <div className="flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-neutral-400 dark:text-neutral-500" />
            <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">Ativar MFA</h2>
          </div>

          <p className="text-sm text-neutral-600 dark:text-neutral-400">
            Se você é admin, MFA é obrigatório para acessar <code>/admin</code>.
          </p>

          <Button
            type="button"
            onClick={() => enrollMutation.mutate()}
            loading={enrollMutation.isPending}
          >
            Iniciar configuração
          </Button>

          {enroll ? (
            <div className="mt-4 space-y-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="font-semibold text-neutral-900 dark:text-white">Secret</h3>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={async () => {
                      try {
                        await copyToClipboard(enroll.secret);
                        addNotification({ type: 'success', message: 'Secret copiado', duration: 2000 });
                      } catch {
                        addNotification({ type: 'warning', message: 'Não foi possível copiar', duration: 3000 });
                      }
                    }}
                    icon={<Copy className="w-4 h-4" />}
                  >
                    Copiar
                  </Button>
                </div>
                <pre className="text-sm p-3 rounded-md bg-neutral-50 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 overflow-x-auto">
                  {enroll.secret}
                </pre>
                <p className="text-xs text-neutral-500 dark:text-neutral-400">
                  Você pode digitar o secret manualmente no autenticador.
                </p>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="font-semibold text-neutral-900 dark:text-white">OTPAuth URI</h3>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={async () => {
                      try {
                        await copyToClipboard(enroll.otpauthUri);
                        addNotification({ type: 'success', message: 'URI copiada', duration: 2000 });
                      } catch {
                        addNotification({ type: 'warning', message: 'Não foi possível copiar', duration: 3000 });
                      }
                    }}
                    icon={<Copy className="w-4 h-4" />}
                  >
                    Copiar
                  </Button>
                </div>
                <pre className="text-xs p-3 rounded-md bg-neutral-50 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 overflow-x-auto">
                  {enroll.otpauthUri}
                </pre>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="font-semibold text-neutral-900 dark:text-white">Códigos de backup (salve agora)</h3>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={async () => {
                      try {
                        await copyToClipboard(backupCodesText);
                        addNotification({ type: 'success', message: 'Códigos copiados', duration: 2000 });
                      } catch {
                        addNotification({ type: 'warning', message: 'Não foi possível copiar', duration: 3000 });
                      }
                    }}
                    icon={<Copy className="w-4 h-4" />}
                  >
                    Copiar
                  </Button>
                </div>
                <pre className="text-sm p-3 rounded-md bg-neutral-50 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 overflow-x-auto">
                  {backupCodesText}
                </pre>
                <p className="text-xs text-neutral-500 dark:text-neutral-400">
                  Cada código funciona uma única vez.
                </p>
              </div>

              <Input
                value={verifyCode}
                onChange={(e) => setVerifyCode(e.target.value)}
                type="text"
                inputMode="numeric"
                placeholder="Código do autenticador (6 dígitos)"
                icon={<ShieldCheck className="w-5 h-5" />}
                autoComplete="one-time-code"
              />

              <Button
                type="button"
                onClick={() => verifyMutation.mutate()}
                loading={verifyMutation.isPending}
                disabled={verifyCode.trim().length < 6}
              >
                Confirmar e ativar MFA
              </Button>
            </div>
          ) : null}
        </div>
      ) : (
        <div className="card p-6 space-y-4">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-neutral-400 dark:text-neutral-500" />
            <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">MFA ativo</h2>
          </div>
          <p className="text-sm text-neutral-600 dark:text-neutral-400">
            MFA está habilitado para sua conta.
          </p>

          <div className="pt-2 border-t border-neutral-200 dark:border-neutral-800 space-y-3">
            <div className="flex items-center gap-2">
              <ShieldOff className="w-5 h-5 text-neutral-400 dark:text-neutral-500" />
              <h3 className="font-semibold text-neutral-900 dark:text-white">Desabilitar MFA</h3>
            </div>
            <p className="text-sm text-neutral-600 dark:text-neutral-400">
              Informe um código do autenticador ou um código de backup.
            </p>
            <Input
              value={disableCode}
              onChange={(e) => setDisableCode(e.target.value)}
              type="text"
              inputMode="numeric"
              placeholder="Código MFA ou backup"
              icon={<ShieldOff className="w-5 h-5" />}
              autoComplete="one-time-code"
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => disableMutation.mutate()}
              loading={disableMutation.isPending}
              disabled={disableCode.trim().length < 6}
            >
              Desabilitar MFA
            </Button>
          </div>
        </div>
      )}

      {user?.isAdmin ? (
        <div className="text-sm text-neutral-500 dark:text-neutral-400">
          <p>
            Dica: Ações críticas (elevar/revogar admin) exigem MFA e justificativa.
          </p>
        </div>
      ) : null}
    </div>
  );
}

